import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Star, Award, Briefcase, Zap, Flame, MousePointerClick, DollarSign } from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

const SalespersonPerformanceCard = ({ salespersonId, token }) => {
  const navigate = useNavigate();
  const [perf, setPerf] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPerf = async () => {
      try {
        const res = await fetch(`${API}/business/salesperson-performance/${salespersonId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) setPerf(await res.json());
      } catch (error) {
        console.error('Failed to fetch performance');
      } finally {
        setLoading(false);
      }
    };
    if (salespersonId && token) fetchPerf();
  }, [salespersonId, token]);

  if (loading || !perf) return null;

  const badgeConfig = {
    GOLD: { label: 'Gold', color: 'text-yellow-400 bg-yellow-400/10 border-yellow-400/30' },
    SILVER: { label: 'Silver', color: 'text-slate-300 bg-slate-300/10 border-slate-300/30' },
    BRONZE: { label: 'Bronze', color: 'text-amber-600 bg-amber-600/10 border-amber-600/30' }
  };
  const b = perf.badge ? badgeConfig[perf.badge] : null;

  return (
    <div className="rounded-xl border border-white/10 bg-[#111317] p-4 mb-4" data-testid="sp-performance-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          {perf.avatar ? (
            <img src={perf.avatar} alt={perf.name} className="w-9 h-9 rounded-full object-cover border border-white/10" />
          ) : (
            <div className="w-9 h-9 rounded-full bg-brand-gold/20 flex items-center justify-center text-brand-gold font-semibold text-sm">
              {perf.name?.charAt(0)?.toUpperCase()}
            </div>
          )}
          <div>
            <button onClick={() => navigate(`/salespeople/${perf.id}`)} className="text-sm font-semibold text-brand-text hover:text-brand-gold transition-colors" data-testid="sp-perf-name">
              {perf.name}
            </button>
            <div className="flex items-center gap-2 mt-0.5">
              {b && (
                <span className={`text-[10px] px-1.5 py-0.5 rounded border font-medium ${b.color}`}>
                  <Award className="w-2.5 h-2.5 inline mr-0.5" />{b.label}
                </span>
              )}
              <span className="flex items-center gap-0.5 text-xs text-brand-muted">
                <Star className="w-3 h-3 text-brand-gold fill-brand-gold" />{perf.avg_rating.toFixed(1)} ({perf.review_count})
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3 text-xs text-brand-muted">
          {perf.xp > 0 && <span className="flex items-center gap-0.5 text-purple-400"><Zap className="w-3 h-3" />{perf.xp} XP</span>}
          {perf.streak > 0 && <span className="flex items-center gap-0.5 text-orange-400"><Flame className="w-3 h-3" />{perf.streak} mo</span>}
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <MiniStat icon={<MousePointerClick className="w-3.5 h-3.5 text-blue-400" />} label="Link Clicks" value={perf.tracking_clicks} testid="sp-perf-clicks" />
        <MiniStat icon={<Briefcase className="w-3.5 h-3.5 text-green-400" />} label="Deals for You" value={perf.deals_for_you} testid="sp-perf-deals" />
        <MiniStat icon={<DollarSign className="w-3.5 h-3.5 text-brand-gold" />} label="Revenue" value={`$${perf.revenue_for_you.toLocaleString()}`} testid="sp-perf-revenue" />
        <MiniStat icon={<Briefcase className="w-3.5 h-3.5 text-brand-muted" />} label="Total Deals" value={perf.total_deals} testid="sp-perf-total" />
      </div>
    </div>
  );
};

const MiniStat = ({ icon, label, value, testid }) => (
  <div className="rounded-lg bg-brand-bg border border-white/5 p-2.5 text-center" data-testid={testid}>
    <div className="flex items-center justify-center gap-1 mb-0.5">
      {icon}
      <span className="text-brand-text font-bold text-sm">{value}</span>
    </div>
    <div className="text-[10px] text-brand-muted">{label}</div>
  </div>
);

export default SalespersonPerformanceCard;
